import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiFetch } from '../../../../../services/api.js';
import { exportAuditLogsCsv } from '../../../../../services/csvExport.js';
import usePersistedColumns from '../../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../../hooks/usePersistedPerPage.js';
import ColumnVisibilityToggle from '../../../../../components/v2/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../../components/v2/ActiveFilterBar.jsx';
import Pagination from '../../../../../components/v2/Pagination.jsx';
import { ariaSort } from '../../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// JSON Value Cell — badge with popover + click-to-copy (matches ServerInfoTab pattern)
// ---------------------------------------------------------------------------
const JsonValueCell = ({ label, value }) => {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);
    const hoverTimeout = useRef(null);

    const handleEnter = () => {
        clearTimeout(hoverTimeout.current);
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverW = 360;
        const spaceBelow = window.innerHeight - rect.bottom;
        const centerX = rect.left + rect.width / 2;
        let left = centerX - popoverW / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

        setStyle({
            position: 'fixed',
            zIndex: 9999,
            left: `${left}px`,
            top: spaceBelow < 160 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 160 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            width: `${popoverW}px`,
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    const handleLeave = () => {
        hoverTimeout.current = setTimeout(() => { setOpen(false); setCopied(false); }, 100);
    };

    const handleCopy = (e) => {
        e.stopPropagation();
        if (navigator?.clipboard) {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => { setOpen(false); setCopied(false); };
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    useEffect(() => () => clearTimeout(hoverTimeout.current), []);

    /* After the hooks, never before: rows are keyed and re-render with new values, and
       an early return above the effects changes the hook count. */
    if (!value || value.trim() === '') {
        return <span className="ap-cell-text--muted">&mdash;</span>;
    }

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={handleCopy}
                aria-label={`Copy ${label}`}
                title="Click to copy"
            >
                <i className="fe fe-clipboard" style={{ fontSize: '12px' }} />
                JSON
            </button>
            {open ? (
                <span className="na-type-popover" style={style}>
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{label}</span>
                        {copied ? (
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', fontWeight: 'var(--font-weight-medium)' }}>Copied!</span>
                        ) : null}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45', wordBreak: 'break-all', maxHeight: '200px', overflowY: 'auto' }}>{value}</span>
                </span>
            ) : null}
        </span>
    );
};

/**
 * Column definitions for the audit logs table.
 */
const AUDIT_COLUMN_DEFS = [
    { key: 'create_time', label: 'Timestamp', sortable: true, filterable: false, required: true },
    { key: 'action', label: 'Action', sortable: true, filterable: true, required: false },
    { key: 'prev_value', label: 'From', sortable: true, filterable: false, required: false },
    { key: 'new_value', label: 'To', sortable: true, filterable: false, required: false },
    { key: 'source', label: 'Source', sortable: true, filterable: true, required: false },
    { key: 'display_name', label: 'User', sortable: true, filterable: true, required: false },
];

const ALL_COLUMN_KEYS = AUDIT_COLUMN_DEFS.map((col) => col.key);
const DEFAULT_VISIBLE = ['create_time', 'action', 'prev_value', 'new_value', 'source', 'display_name'];

/** Column labels for ActiveFilterBar */
const COLUMN_LABELS = {};
AUDIT_COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

/** Auto-refresh interval in milliseconds (10 minutes) */
const REFRESH_INTERVAL = 600_000;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AuditLogsTab = ({ campusId, myCampusId }) => {
    // -- Data --
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // -- Table state --
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('create_time');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('campus-audit-logs', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);

    // -- Pagination --
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // ======================================================================
    // Data loading
    // ======================================================================
    const fetchLogs = useCallback(async () => {
        try {
            const data = await apiFetch({
                procedure: 'getauditlogsbycampusid',
                reqType: 'stored',
                pKey: 'campus_id',
                pVal: myCampusId,
                payload: { campus_id: campusId },
                readOnly: true,
            });
            setLogs(data || []);
        } catch (err) {
            console.error('[AuditLogsTab] Failed to fetch audit logs:', err);
        } finally {
            setLoading(false);
        }
    }, [campusId, myCampusId]);

    // Initial load
    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Auto-refresh every 10 minutes
    const intervalRef = useRef(null);
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            fetchLogs();
        }, REFRESH_INTERVAL);
        return () => clearInterval(intervalRef.current);
    }, [fetchLogs]);

    // Clear selection on data reload
    useEffect(() => {
        setSelectedIds([]);
    }, [logs]);

    // Reset page on filter/search/perPage changes
    useEffect(() => {
        setPage(1);
    }, [search, filters, perPage]);

    // ======================================================================
    // Date formatting
    // ======================================================================
    const formatDate = useCallback((utcStr) => {
        if (!utcStr) return '—';
        try {
            const d = new Date(utcStr);
            return d.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch {
            return utcStr;
        }
    }, []);

    // ======================================================================
    // Active columns
    // ======================================================================
    const activeColumns = useMemo(
        () => AUDIT_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    // Collect unique filter values from full dataset
    const getFilterValues = useCallback(
        (columnKey) => {
            const values = new Set();
            logs.forEach((row) => {
                const val = row[columnKey];
                values.add(val != null && val !== '' ? String(val) : '(empty)');
            });
            return [...values].sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            );
        },
        [logs]
    );

    // ======================================================================
    // Filtered + sorted data pipeline
    // ======================================================================
    const filteredData = useMemo(() => {
        let data = [...logs];

        // 1. Search
        if (search.trim()) {
            const term = search.toLowerCase();
            data = data.filter(
                (row) =>
                    (row.action || '').toLowerCase().includes(term) ||
                    (row.prev_value || '').toLowerCase().includes(term) ||
                    (row.new_value || '').toLowerCase().includes(term) ||
                    (row.source || '').toLowerCase().includes(term) ||
                    (row.display_name || '').toLowerCase().includes(term) ||
                    (row.create_time || '').toLowerCase().includes(term)
            );
        }

        // 2. Column filters
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            data = data.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        // 3. Sort
        data.sort((a, b) => {
            const aVal = a[sortColumn] || '';
            const bVal = b[sortColumn] || '';
            const result = String(aVal).localeCompare(String(bVal), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return sortOrder === 'asc' ? result : -result;
        });

        return data;
    }, [logs, search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Pagination
    // ======================================================================
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);

    const paginatedData = useMemo(() => {
        const start = (safePage - 1) * perPage;
        return filteredData.slice(start, start + perPage);
    }, [filteredData, safePage, perPage]);

    // ======================================================================
    // Selection (operates on current page)
    // ======================================================================
    const allSelected = paginatedData.length > 0 && paginatedData.every((r) => selectedIds.includes(r.audit_log_id ?? r.create_time));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const getRowId = (row) => row.audit_log_id ?? row.create_time;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = paginatedData.map(getRowId);
        const currentAllSelected = allIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
        }
    }, [paginatedData, selectedIds]);

    // ======================================================================
    // Sort / Filter / Column handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({
            ...prev,
            [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
        }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({});
        setSearch('');
    }, []);

    // ======================================================================
    // Export
    // ======================================================================
    const prepareExportRows = useCallback((rows) => {
        return rows.map((r) => ({
            ...r,
            _create_time: formatDate(r.create_time),
        }));
    }, [formatDate]);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportAuditLogsCsv(prepareExportRows(filteredData));
    }, [filteredData, prepareExportRows]);

    const handleBulkExport = useCallback(() => {
        const selected = logs.filter((r) => selectedIds.includes(getRowId(r)));
        if (selected.length) exportAuditLogsCsv(prepareExportRows(selected), 'audit-logs-selected.csv');
    }, [selectedIds, logs, prepareExportRows]);

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    /** Render a single cell value */
    const renderCell = (row, columnKey) => {
        if (columnKey === 'create_time') {
            return <span className="ap-cell-text">{formatDate(row.create_time)}</span>;
        }
        if (columnKey === 'prev_value') {
            return <JsonValueCell label="From (Previous Value)" value={row.prev_value} />;
        }
        if (columnKey === 'new_value') {
            return <JsonValueCell label="To (New Value)" value={row.new_value} />;
        }
        return <span className="ap-cell-text">{row[columnKey] || '—'}</span>;
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    if (loading) {
        return (
            <div className="pp-section">
                <div className="pp-section__body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="dp-skeleton" style={{ width: i % 2 === 0 ? '100%' : '70%', height: '16px' }} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search logs..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="ap-search__input"
                            aria-label="Search audit logs"
                        />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle
                        columns={AUDIT_COLUMN_DEFS}
                        visibleColumns={visibleColumns}
                        onToggle={handleColumnToggle}
                    />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export audit logs">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                    <button
                        type="button"
                        className="ap-btn ap-btn--secondary"
                        onClick={() => { setLoading(true); fetchLogs(); }}
                        aria-label="Refresh audit logs"
                    >
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
            />

            {/* Bulk action bar */}
            {selectedIds.length > 0 ? (
                <div className="ap-bulk-bar">
                    <div className="ap-bulk-bar__left">
                        <span className="ap-bulk-bar__count">
                            {selectedIds.length} log{selectedIds.length !== 1 ? 's' : ''} selected
                        </span>
                        <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">
                            Deselect All
                        </button>
                    </div>
                    <div className="ap-bulk-bar__right">
                        <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected logs">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Export
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Table */}
            {paginatedData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters
                            ? 'No logs match your filters.'
                            : 'No audit logs found for this campus.'}
                    </p>
                </div>
            ) : (
                <div className="ap-table-wrap">
                    <table className="ap-table">
                        <thead className="ap-thead">
                            <tr>
                                {/* Checkbox column */}
                                <th scope="col" className="ap-th ap-th--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                        onChange={handleSelectAll}
                                        className="ap-checkbox"
                                        aria-label="Select all logs on this page"
                                    />
                                </th>

                                {/* Data columns */}
                                {activeColumns.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(col.key)}
                                                    className="ap-th__sort-btn"
                                                    aria-label={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    {renderSortIcon(col.key)}
                                                </button>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}

                                            {col.filterable ? (
                                                <FilterPopover
                                                    columnKey={col.key}
                                                    label={col.label}
                                                    values={getFilterValues(col.key)}
                                                    selectedValues={filters[col.key] || []}
                                                    onChange={handleFilterChange}
                                                />
                                            ) : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className="ap-tbody">
                            {paginatedData.map((row, idx) => {
                                const rowId = getRowId(row);
                                const isSelected = selectedIds.includes(rowId);
                                return (
                                    <tr
                                        key={rowId ?? idx}
                                        className={`ap-tr ${isSelected ? 'is-selected' : ''}`}
                                    >
                                        {/* Checkbox */}
                                        <td className="ap-td ap-td--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleSelectRow(rowId)}
                                                className="ap-checkbox"
                                                aria-label="Select log entry"
                                            />
                                        </td>

                                        {/* Data cells */}
                                        {activeColumns.map((col) => (
                                            <td key={col.key} className="ap-td">
                                                {renderCell(row, col.key)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalFiltered > 0 ? (
                <Pagination page={safePage} totalPages={totalPages} total={totalFiltered} perPage={perPage} onPageChange={setPage} onPerPageChange={setPerPage} entityLabel="logs" selectId="audit-per-page" />
            ) : null}
        </>
    );
};

export default AuditLogsTab;
